import React, { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { AuthGuard } from "@/routes/guards/auth-guard";
import { DashboardLayout } from "@/routes/layouts/dashboard-layout";

// ── Lazy page chunks ──────────────────────────────────────────────────────────
// Each route becomes its own JS chunk, downloaded only when first visited.
// lazyLoad wraps each import: if the chunk is missing (stale cache after deploy),
// it forces a hard reload once so the browser fetches the new hashed filenames.
function lazyLoad<T>(factory: () => Promise<{ default: T }>): React.LazyExoticComponent<React.ComponentType<object>> {
  return lazy(() =>
    factory().catch((err) => {
      const reloadKey = "chunk_reload_attempted";
      if (!sessionStorage.getItem(reloadKey)) {
        sessionStorage.setItem(reloadKey, "1");
        window.location.reload();
        return new Promise(() => {});
      }
      throw err;
    }) as Promise<{ default: React.ComponentType<object> }>
  );
}

// Public site
const HomePage       = lazyLoad(() => import("@/modules/home/pages/home").then(m => ({ default: m.HomePage })));
const ProgramsPage   = lazyLoad(() => import("@/modules/home/pages/programs").then(m => ({ default: m.ProgramsPage })));
const CourtsPage     = lazyLoad(() => import("@/modules/home/pages/courts").then(m => ({ default: m.CourtsPage })));

// Auth
const LoginPage          = lazyLoad(() => import("@/modules/auth/pages/login").then(m => ({ default: m.LoginPage })));
const ForgotPasswordPage = lazyLoad(() => import("@/modules/auth/pages/forgot-password").then(m => ({ default: m.ForgotPasswordPage })));

// Dashboard (protected — heaviest chunk, contains recharts)
const DashboardPage      = lazyLoad(() => import("@/modules/dashboard/pages/dashboard").then(m => ({ default: m.DashboardPage })));
const PlayersPage        = lazyLoad(() => import("@/modules/players/pages/players").then(m => ({ default: m.PlayersPage })));
const NewPlayerPage      = lazyLoad(() => import("@/modules/players/pages/players-new").then(m => ({ default: m.NewPlayerPage })));
const PlayerDetailPage   = lazyLoad(() => import("@/modules/players/pages/players-detail").then(m => ({ default: m.PlayerDetailPage })));
const InvoicesPage       = lazyLoad(() => import("@/modules/invoices/pages/invoices").then(m => ({ default: m.InvoicesPage })));
const BillingPage        = lazyLoad(() => import("@/modules/billing/pages/billing").then(m => ({ default: m.BillingPage })));
const RemindersPage      = lazyLoad(() => import("@/modules/reminders/pages/reminders").then(m => ({ default: m.RemindersPage })));
const TournamentPage     = lazyLoad(() => import("@/modules/tournament/pages/tournament").then(m => ({ default: m.TournamentPage })));
const SettingsPage       = lazyLoad(() => import("@/modules/settings/pages/settings").then(m => ({ default: m.SettingsPage })));
const WhatsAppSettingsPage = lazyLoad(() => import("@/modules/settings/pages/settings-whatsapp").then(m => ({ default: m.WhatsAppSettingsPage })));

// ── Navigation breadcrumb tracker for Sentry ─────────────────────────────────
function SentryRouteTracker() {
  const location = useLocation();
  useEffect(() => {
    Sentry.addBreadcrumb({
      category: "navigation",
      message: location.pathname,
      level: "info",
    });
  }, [location]);
  return null;
}

// ── Fallback shown while a lazy chunk loads ───────────────────────────────────
function PageLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

export function Router() {
  return (
    <BrowserRouter>
      <SentryRouteTracker />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public routes */}
          <Route path="/"               element={<HomePage />} />
          <Route path="/programs"       element={<ProgramsPage />} />
          <Route path="/courts"         element={<CourtsPage />} />
          <Route path="/login"          element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />

          {/* Protected routes — requires valid session cookie */}
          <Route element={<AuthGuard />}>
            <Route element={<DashboardLayout />}>
              <Route path="/dashboard"          element={<DashboardPage />} />
              <Route path="/players"            element={<PlayersPage />} />
              <Route path="/players/new"        element={<NewPlayerPage />} />
              <Route path="/players/:id"        element={<PlayerDetailPage />} />
              <Route path="/invoices"           element={<InvoicesPage />} />
              <Route path="/billing"            element={<BillingPage />} />
              <Route path="/reminders"          element={<RemindersPage />} />
              <Route path="/tournament"         element={<TournamentPage />} />
              <Route path="/settings"           element={<SettingsPage />} />
              <Route path="/settings/whatsapp"  element={<WhatsAppSettingsPage />} />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
